import { forwardRef } from 'react';

import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';

import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerProps,
  Spinner,
  Stack,
} from '@cast/design-system';
import { OptimizationWorkload } from '@cast/types';

import { CostReportWorkloadDetailsDrawerContent } from 'features/cluster/cost-report/workloads/components/CostReportWorkloadDetailsDrawerContent';
import { getWorkloadQueryOptions } from 'hooks/queries/workload-optimization/useWorkloadQuery';

import { useCluster } from '@cast/shared-cluster-context-data-access';

type BaseProps = {
  rawWorkload: OptimizationWorkload;
};

type Props = DrawerProps & BaseProps;

export const CostReportWorkloadDetailsDrawer = forwardRef<
  HTMLDivElement,
  Props
>(({ rawWorkload, ...drawerProps }, ref) => {
  const { cluster } = useCluster();

  const now = dayjs.utc();
  const { data: pods, isLoading: isPodsLoading } = useQuery({
    ...getWorkloadQueryOptions({
      clusterId: cluster.id,
      workloadId: rawWorkload.id,
      includeMetrics: false,
      includeCosts: false,
      key: 'pods-only',
      selection: {
        range: {
          from: now.subtract(1, 'hour').toISOString(),
          to: now.toISOString(),
        },
      },
      staleTime: dayjs.duration(5, 'minutes').asMilliseconds(),
    }),
    select: (data) => data.pods ?? [],
  });

  return (
    <Drawer
      ref={ref}
      size="large"
      testId="workload-details-drawer"
      {...drawerProps}
    >
      {isPodsLoading ? (
        <DrawerBody height="100%">
          <DrawerContent sx={{ height: '100%' }}>
            <Stack alignItems="center" justifyContent="center" height="100%">
              <Spinner variant="indeterminate" size={32} />
            </Stack>
          </DrawerContent>
        </DrawerBody>
      ) : (
        <CostReportWorkloadDetailsDrawerContent
          workload={rawWorkload}
          pods={pods ?? []}
        />
      )}
    </Drawer>
  );
});

CostReportWorkloadDetailsDrawer.displayName = 'CostReportWorkloadDetailsDrawer';
